import type {
  AxisContribution,
  LevelAssessmentResult,
  MovementSummary,
  MovementSummaryAxis,
} from "@shared/labs/level-assessment";
import {
  ArrowDownRight,
  ArrowUpRight,
  BarChart3,
  ChevronDown,
  ChevronRight,
  History,
  TrendingUpDown,
} from "lucide-react";
import { useState } from "react";
import { LevelChip } from "./chips";
import { levelDistribution, synthesizeOverallWhy } from "./derive";
import { DetailShell, Eyebrow, FieldBlock } from "./detail-shell";
import { LevelWhisker } from "./level-whisker";
import { FragileChip, OverallExplanationPanel, SPREAD_LABEL } from "./overall-explanation-panel";
import { C, confidenceTone, DM, levelRange, UI } from "./ui-tokens";
import { WhyPanel } from "./why-panel";

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

/** Pulls at/below this magnitude (in level units) are treated as "on the
 *  estimate" — they neither lift nor lower it materially, so they stay out of
 *  the distilled driver cards and live only in the full breakdown. */
const MATERIAL_PULL = 0.05;

/** Bars: count of scored axes per level, with the assigned level outlined and
 *  the estimate band tinted red. */
function DistributionBars({
  result,
  levels,
}: {
  result: LevelAssessmentResult;
  levels: { min: number; max: number };
}) {
  const dist = levelDistribution(result);
  const assigned = result.overallLevel;
  // Band = μ ± σ of the weighted distribution (persisted overallStddev).
  // Legacy rows (field absent) fall back to averaging section std-devs.
  const stddevs = [result.cross.stddev, result.vertical.stddev].filter(
    (s): s is number => s !== null,
  );
  const legacyS = stddevs.length ? stddevs.reduce((a, b) => a + b, 0) / stddevs.length : 0;
  const s = result.overallStddev !== undefined ? (result.overallStddev ?? 0) : legacyS;
  const mean = result.overallMean;
  const bMin = mean === null ? null : clamp(Math.floor(mean - s), levels.min, levels.max);
  const bMax = mean === null ? null : clamp(Math.ceil(mean + s), levels.min, levels.max);

  const counts = levelRange(levels).map((n) => dist.get(n) ?? 0);
  const maxCount = Math.max(1, ...counts);
  const H = 96;

  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 8, height: H + 34 }}>
      {levelRange(levels).map((n) => {
        const cnt = dist.get(n) ?? 0;
        const inBand = bMin !== null && bMax !== null && n >= bMin && n <= bMax;
        const isAssigned = n === assigned;
        const barH = cnt === 0 ? 3 : Math.round((cnt / maxCount) * H);
        return (
          <div
            key={n}
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 6,
            }}
          >
            <span
              style={{
                ...DM,
                fontWeight: 700,
                fontSize: 12,
                color: cnt ? C.ink : C.ink20,
                height: 16,
              }}
            >
              {cnt || ""}
            </span>
            <div
              style={{
                width: "100%",
                maxWidth: 40,
                height: barH,
                borderRadius: 6,
                background: cnt === 0 ? C.ink5 : inBand ? C.red : C.ink20,
                border: isAssigned ? `2px solid ${C.ink}` : "none",
                transition: "height 200ms",
              }}
            />
            <span
              style={{
                ...DM,
                fontWeight: isAssigned ? 700 : 600,
                fontSize: 12,
                color: inBand ? C.red : C.ink50,
              }}
            >
              L{n}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/** The lead read: the overall-explainer's one-line synthesis (Member C). Kept
 *  modest — it is a sentence to read, not a banner. */
function VerdictLead({ summary }: { summary: string }) {
  return (
    <div
      style={{
        background: C.paper,
        borderRadius: 12,
        padding: "16px 18px",
        display: "flex",
        gap: 12,
        alignItems: "flex-start",
      }}
    >
      <span
        style={{
          flex: "none",
          width: 4,
          alignSelf: "stretch",
          borderRadius: 999,
          background: C.red,
        }}
      />
      <span style={{ ...UI, fontSize: 15, fontWeight: 500, color: C.ink, lineHeight: 1.6 }}>
        {summary}
      </span>
    </div>
  );
}

/** Lead dispersion + confidence read: a larger version of the header whisker
 *  (each scored competency plotted as a tick, the estimate band over it), the
 *  spread band label, a plain-language confidence figure, and the explainer's
 *  risk sentence. Replaces the technical histogram + threshold meter at the top
 *  of the screen — those move into the collapsed breakdown. */
function DispersionConfidence({
  mean,
  stddev,
  points,
  levels,
  spread,
  risk,
  confidence,
  graded,
  total,
  ndNames,
  weightPct,
}: {
  mean: number | null;
  stddev: number | null;
  points: number[];
  levels: { min: number; max: number };
  spread?: "tight" | "moderate" | "wide";
  risk?: string;
  confidence: number;
  graded: number;
  total: number;
  ndNames: string[];
  weightPct: number | null;
}) {
  const tone = confidenceTone(confidence);
  const pct = Math.round(confidence * 100);
  const spreadStyle = {
    tight: { fg: C.green, bg: C.greenBg },
    moderate: { fg: C.amberFg, bg: C.amberBg },
    wide: { fg: C.red, bg: C.red10 },
  } as const;
  const sp = spread ? spreadStyle[spread] : { fg: C.ink50, bg: C.ink5 };
  const coverageLine =
    (weightPct !== null ? `cobertura ${weightPct}% do peso · ` : "") +
    `${graded} de ${total} competências avaliadas`;

  return (
    <div
      style={{
        background: C.paper,
        borderRadius: 12,
        padding: "16px 18px 18px",
        display: "flex",
        flexDirection: "column",
        gap: 14,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <TrendingUpDown size={15} color={C.ink50} />
        <Eyebrow>Como as competências se distribuem</Eyebrow>
        {spread && (
          <span
            style={{
              ...UI,
              fontWeight: 600,
              fontSize: 11,
              padding: "2px 9px",
              borderRadius: 999,
              background: sp.bg,
              color: sp.fg,
              marginLeft: 2,
            }}
          >
            {SPREAD_LABEL[spread]}
          </span>
        )}
      </div>

      <div style={{ width: "100%", maxWidth: 560 }}>
        <LevelWhisker
          mean={mean}
          stddev={stddev}
          levels={levels}
          width={520}
          size="lg"
          points={points}
        />
        <div style={{ ...UI, fontSize: 11, color: C.ink50, marginTop: 4 }}>
          cada traço é uma competência · barra = faixa estimada · ponto = nível geral
        </div>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: 10,
          flexWrap: "wrap",
          borderTop: `1px solid ${C.borderSoft}`,
          paddingTop: 13,
        }}
      >
        <span style={{ ...UI, fontWeight: 600, fontSize: 12.5, color: C.ink }}>
          Confiança nesta leitura
        </span>
        <span style={{ ...DM, fontWeight: 700, fontSize: 22, color: tone.fg, lineHeight: 1 }}>
          {pct}%
        </span>
        <span style={{ ...UI, fontSize: 12, color: C.ink50 }}>{coverageLine}</span>
      </div>

      {risk && <div style={{ ...UI, fontSize: 13, color: C.ink70, lineHeight: 1.55 }}>{risk}</div>}
      {ndNames.length > 0 && (
        <div style={{ ...UI, fontSize: 12.5, color: C.ink70, lineHeight: 1.5 }}>
          <b style={{ color: C.ink }}>{ndNames.join(", ")}</b> ficou em N/D — verificar em
          entrevista, não entra no cálculo.
        </div>
      )}
    </div>
  );
}

/** One distilled-driver card: the material movers on a side, named with their
 *  level, plus the top mover's interpretation. The full per-competency reasons
 *  live in the collapsed breakdown. */
function DriverCard({ kind, items }: { kind: "up" | "down"; items: AxisContribution[] }) {
  const material = items.filter((c) => Math.abs(c.pull) >= MATERIAL_PULL);
  const shown = material.slice(0, 3);
  const cfg =
    kind === "up"
      ? { title: "O que sustenta o nível", fg: C.greenFg, bg: C.greenBg, Icon: ArrowUpRight }
      : { title: "O que pressiona para baixo", fg: C.red, bg: C.red10, Icon: ArrowDownRight };
  const { Icon } = cfg;
  const empty =
    kind === "up"
      ? "Nenhuma competência eleva a estimativa de forma relevante."
      : "Nenhuma competência puxa a estimativa para baixo — as demais convergem no nível.";
  return (
    <div
      style={{
        background: cfg.bg,
        borderRadius: 12,
        padding: "15px 17px",
        display: "flex",
        flexDirection: "column",
        gap: 11,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
        <Icon size={15} color={cfg.fg} />
        <span style={{ ...DM, fontWeight: 700, fontSize: 13.5, color: cfg.fg }}>{cfg.title}</span>
      </div>
      {shown.length === 0 ? (
        <div style={{ ...UI, fontSize: 12.5, color: C.ink70, lineHeight: 1.5 }}>{empty}</div>
      ) : (
        <>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
            {shown.map((c) => (
              <span key={c.axis} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <LevelChip level={`L${c.level}`} />
                <span style={{ ...DM, fontWeight: 600, fontSize: 13, color: C.ink }}>
                  {c.axisLabel}
                </span>
                {c.reliability === "fragile" && <FragileChip />}
              </span>
            ))}
            {material.length > shown.length && (
              <span style={{ ...UI, fontSize: 12, color: C.ink50 }}>
                +{material.length - shown.length}
              </span>
            )}
          </div>
          {shown[0]?.interpretation && (
            <div style={{ ...UI, fontSize: 12.5, color: C.ink70, lineHeight: 1.5 }}>
              {shown[0].interpretation}
            </div>
          )}
        </>
      )}
    </div>
  );
}

/** Disclosure wrapper: a labelled row that toggles its content. Collapsed by
 *  default — the detailed breakdown is opt-in. */
function Collapsible({
  label,
  sublabel,
  children,
}: {
  label: string;
  sublabel?: string;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          background: C.white,
          border: `1px solid ${C.border}`,
          borderRadius: 12,
          padding: "13px 16px",
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: 9, flexWrap: "wrap" }}>
          <BarChart3 size={16} color={C.ink50} />
          <span style={{ ...DM, fontWeight: 700, fontSize: 13.5, color: C.ink }}>{label}</span>
          {sublabel && <span style={{ ...UI, fontSize: 12, color: C.ink50 }}>{sublabel}</span>}
        </span>
        {open ? (
          <ChevronDown size={18} color={C.ink50} />
        ) : (
          <ChevronRight size={18} color={C.ink50} />
        )}
      </button>
      {open && children}
    </div>
  );
}

/** One axis's from→to line inside the round-story section — the compact
 *  `MovementSummaryAxis` projection (label + verdict only). Mirrors the
 *  `MovementChip` wording ("Entrou em L{x}" from abstention) so the same
 *  movement reads identically everywhere it appears. The per-axis EVIDENCE
 *  (the P3 rationale) is deliberately NOT shown here — it is audit text that
 *  cites internal claim ids, and its full, readable form lives inside each
 *  competency card below. This section is the overall movement view only. */
function AxisMovementLine({ axis }: { axis: MovementSummaryAxis }) {
  const moveLabel =
    axis.toLevel === null
      ? "sem nível"
      : axis.fromLevel === null
        ? `Entrou em L${axis.toLevel}`
        : axis.fromLevel === axis.toLevel
          ? `L${axis.toLevel}`
          : `L${axis.fromLevel} → L${axis.toLevel}`;
  return (
    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "baseline" }}>
      <span style={{ ...DM, fontWeight: 600, fontSize: 13, color: C.ink }}>{axis.axisLabel}</span>
      <span style={{ ...UI, fontSize: 12, fontWeight: 600, color: C.red, whiteSpace: "nowrap" }}>
        {moveLabel}
      </span>
    </div>
  );
}

/** "O que mudou nesta rodada" — the fusion round's own voice (S3, the
 *  narrative layer's Member-C-fusion upgrade), rendered ABOVE the
 *  comparative/axis area whenever the persisted result carries a
 *  `movementSummary` (docs/labs/level-assessment/multi-round-narrative-layer.md
 *  §2/§3). Degrades field-by-field: an absent `headline`/`nextFocus` (older
 *  persisted rows, or a degraded S3 call that only filled `narrative`) just
 *  skips that element rather than rendering an empty box. */
function MovementStorySection({ movement }: { movement: MovementSummary }) {
  return (
    <div
      style={{
        background: C.paper,
        borderRadius: 12,
        padding: "16px 18px 18px",
        display: "flex",
        flexDirection: "column",
        gap: 14,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <History size={15} color={C.ink50} />
        <Eyebrow>O que mudou nesta rodada</Eyebrow>
      </div>

      {movement.headline && (
        <div style={{ ...DM, fontWeight: 700, fontSize: 16, color: C.ink, lineHeight: 1.4 }}>
          {movement.headline}
        </div>
      )}

      {movement.narrative && (
        <p style={{ ...UI, fontSize: 13.5, color: C.ink70, lineHeight: 1.6, margin: 0 }}>
          {movement.narrative}
        </p>
      )}

      {movement.axes.length > 0 && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            borderTop: `1px solid ${C.borderSoft}`,
            paddingTop: 12,
          }}
        >
          {movement.axes.map((a) => (
            <AxisMovementLine key={a.axisId} axis={a} />
          ))}
        </div>
      )}

      {movement.axisHighlights && movement.axisHighlights.length > 0 && (
        <ul
          style={{
            margin: 0,
            paddingLeft: 18,
            display: "flex",
            flexDirection: "column",
            gap: 6,
          }}
        >
          {movement.axisHighlights.map((h) => (
            <li key={h} style={{ ...UI, fontSize: 13, color: C.ink70, lineHeight: 1.5 }}>
              {h}
            </li>
          ))}
        </ul>
      )}

      {movement.nextFocus && (
        <div
          style={{
            ...UI,
            fontSize: 13,
            fontWeight: 500,
            color: C.ink,
            lineHeight: 1.55,
            borderTop: `1px solid ${C.borderSoft}`,
            paddingTop: 12,
          }}
        >
          Próximos focos:{" "}
          <span style={{ fontWeight: 400, color: C.ink70 }}>{movement.nextFocus}</span>
        </div>
      )}
    </div>
  );
}

export interface LevelDetailProps {
  result: LevelAssessmentResult;
  onClose: () => void;
}

/** Expanded reading of the overall level: a plain-language verdict, a
 *  dispersion + confidence read, the distilled drivers, and — collapsed — the
 *  full per-competency breakdown (distribution + every contribution). */
export function LevelDetail({ result, onClose }: LevelDetailProps) {
  const levels = result.modelSnapshot.levels;
  const labelById = new Map<string, string>();
  for (const a of result.modelSnapshot.crossAxes) labelById.set(a.id, a.label);
  for (const a of result.modelSnapshot.discipline?.axes ?? []) labelById.set(a.id, a.label);

  const allAxes = [...result.cross.axes, ...result.vertical.axes];
  const scored = allAxes.filter((a) => a.inferredLevel !== null);
  const graded = scored.length;
  const total = allAxes.length;
  const points = scored.map((a) => a.inferredLevel as number);
  const ndNames = allAxes
    .filter((a) => a.inferredLevel === null)
    .map((a) => labelById.get(a.axis) ?? a.axis);

  // Prefer the mean/dispersion explanation (which axes pull the average down/up
  // + dispersion risk, framed vs the estimate). Older persisted rows lack it —
  // fall back to the legacy count-based comparative.
  const overallExplanation = result.overallExplanation;
  const why = overallExplanation ? null : synthesizeOverallWhy(result, levels);
  // Weighted runs carry attribution coverage — the meter reads weight mass;
  // legacy rows fall back to the axis count alone.
  const weightPct = overallExplanation?.coverage
    ? Math.round(overallExplanation.coverage.scoredWeightMass * 100)
    : null;
  const assigned = result.overallLevel;

  // Whisker geometry (same source as the header): persisted weighted σ, or the
  // averaged section std-devs on legacy rows.
  const stddevs = [result.cross.stddev, result.vertical.stddev].filter(
    (s): s is number => s !== null,
  );
  const legacyStddev = stddevs.length ? stddevs.reduce((a, b) => a + b, 0) / stddevs.length : null;
  const overallStddev = result.overallStddev !== undefined ? result.overallStddev : legacyStddev;

  const title =
    assigned === null
      ? "Sem nível geral"
      : overallExplanation
        ? `Por que estimamos L${assigned}`
        : why
          ? `Por que L${assigned}, e não ${why.down.level} ou ${why.up.level}?`
          : `Nível geral L${assigned}`;

  return (
    <DetailShell title={title} badge="Nível geral" onClose={onClose}>
      {result.movementSummary && <MovementStorySection movement={result.movementSummary} />}

      {overallExplanation ? (
        <>
          {overallExplanation.summary && <VerdictLead summary={overallExplanation.summary} />}

          <DispersionConfidence
            mean={result.overallMean}
            stddev={overallStddev}
            points={points}
            levels={levels}
            spread={overallExplanation.dispersion?.spread}
            risk={overallExplanation.dispersion?.risk}
            confidence={result.overallConfidence}
            graded={graded}
            total={total}
            ndNames={ndNames}
            weightPct={weightPct}
          />

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <DriverCard kind="up" items={overallExplanation.pullingUp} />
            <DriverCard kind="down" items={overallExplanation.pullingDown} />
          </div>

          <Collapsible
            label="Detalhamento por competência"
            sublabel="distribuição dos níveis · todas as competências"
          >
            <FieldBlock label="Níveis por competência" icon={BarChart3}>
              <div style={{ padding: "16px 18px 12px", background: C.paper, borderRadius: 12 }}>
                <DistributionBars result={result} levels={levels} />
                <div
                  style={{
                    display: "flex",
                    gap: 14,
                    marginTop: 10,
                    ...UI,
                    fontSize: 11,
                    color: C.ink50,
                    flexWrap: "wrap",
                  }}
                >
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                    <span style={{ width: 10, height: 10, borderRadius: 3, background: C.red }} />{" "}
                    faixa estimada
                  </span>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                    <span
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: 3,
                        border: `2px solid ${C.ink}`,
                      }}
                    />{" "}
                    nível geral
                  </span>
                  {total - graded > 0 && (
                    <span>{total - graded} competência(s) em N/D — não entram no cálculo</span>
                  )}
                </div>
              </div>
            </FieldBlock>
            <OverallExplanationPanel data={overallExplanation} />
          </Collapsible>
        </>
      ) : (
        <>
          {result.overallExplanationError && (
            <div
              style={{
                ...UI,
                fontSize: 12.5,
                color: C.amberFg,
                background: C.amberBg,
                borderRadius: 10,
                padding: "10px 14px",
                lineHeight: 1.5,
              }}
            >
              Explicação detalhada indisponível nesta avaliação — exibindo resumo simplificado.
            </div>
          )}
          {why && <WhyPanel data={why} />}
        </>
      )}
    </DetailShell>
  );
}
